import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Edit2, Trash2, Plus, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError, API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { B2BUsersPanel, B2BRequestsPanel, B2BDocsPanel, B2BCatsGroupsPanel, B2BCommsPanel, B2BActivityPanel } from "./AdminB2BPanels";

const TABS = [
  { id: "products", label: "Prodotti" },
  { id: "articles", label: "Articoli" },
  { id: "faqs", label: "FAQ" },
  { id: "reviews", label: "Recensioni" },
  { id: "leads", label: "Lead" },
  { id: "b2b-users", label: "Utenti B2B" },
  { id: "b2b-requests", label: "Richieste B2B" },
  { id: "b2b-docs", label: "Documenti B2B" },
  { id: "b2b-cats", label: "Cat./Gruppi B2B" },
  { id: "b2b-comms", label: "Comunicazioni B2B" },
  { id: "b2b-log", label: "Registro B2B" },
  { id: "settings", label: "Impostazioni" },
];

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");

  const doLogout = () => {
    logout();
    toast.success("Sessione chiusa");
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-soft bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="text-xs uppercase tracking-editorial text-muted-ink hidden md:inline">
              Admin · {user?.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-brand link-underline hidden md:inline">
              Vai al sito
            </Link>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-full border border-soft px-4 py-1.5 text-xs uppercase tracking-editorial text-brand hover:bg-cream-warm transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Esci
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <h1 className="font-display text-4xl text-brand mb-6">Dashboard</h1>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-8 bg-white border border-soft h-auto p-1 rounded-none flex-wrap">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`admin-tab-${t.id}`}
                className="rounded-none data-[state=active]:bg-brand data-[state=active]:text-white text-xs uppercase tracking-editorial px-4 py-2"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="products"><ProductsPanel /></TabsContent>
          <TabsContent value="articles"><ArticlesPanel /></TabsContent>
          <TabsContent value="faqs"><FaqsPanel /></TabsContent>
          <TabsContent value="reviews"><ReviewsPanel /></TabsContent>
          <TabsContent value="leads"><LeadsPanel /></TabsContent>
          <TabsContent value="b2b-users"><B2BUsersPanel /></TabsContent>
          <TabsContent value="b2b-requests"><B2BRequestsPanel /></TabsContent>
          <TabsContent value="b2b-docs"><B2BDocsPanel /></TabsContent>
          <TabsContent value="b2b-cats"><B2BCatsGroupsPanel /></TabsContent>
          <TabsContent value="b2b-comms"><B2BCommsPanel /></TabsContent>
          <TabsContent value="b2b-log"><B2BActivityPanel /></TabsContent>
          <TabsContent value="settings"><SettingsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ---------- Products ---------- */
function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api.get("/admin/products").then((r) => setItems(r.data || [])).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      {editing ? (
        <ProductForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-muted-ink">{items.length} prodotti totali</div>
            <button
              onClick={() => setEditing("new")}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial hover:bg-[#0f2020]"
              data-testid="admin-product-new"
            >
              <Plus className="h-3.5 w-3.5" />
              Nuovo prodotto
            </button>
          </div>
          <div className="bg-white border border-soft overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-warm">
                <tr>
                  <Th>Nome</Th><Th>Slug</Th><Th>Linea</Th><Th>Status</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-soft">
                    <Td>{p.name}</Td>
                    <Td className="text-muted-ink font-mono text-xs">{p.slug}</Td>
                    <Td>
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-editorial ${p.line === "pet" ? "bg-pet-soft text-pet" : "bg-human-soft text-human"}`}>
                        {p.line}
                      </span>
                    </Td>
                    <Td className="text-xs">{p.status}</Td>
                    <Td className="text-right">
                      <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-cream-warm" aria-label="Modifica">
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => del(p.id)} className="p-1.5 hover:bg-cream-warm text-destructive" aria-label="Elimina">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const empty = {
    name: "", slug: "", line: "pet", category: "", short_description: "", long_description: "",
    quantity_label: "", image: "", amazon_url: "", status: "draft",
    badges: [], benefits: [], species: [], analytical_components: [], dosage: [], warnings: [],
    ingredients_composition: "", meta_title: "", meta_description: "",
    approval_status: "pending", approval_notes: "", translations: {}, gallery: [], additives: [],
    other_retailers: [], faq_ids: [], related_slugs: [],
  };
  const [form, setForm] = useState(initial || empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setArr = (k, str) => set(k, str.split("\n").filter(Boolean));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/products/${initial.id}`, form);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", form);
        toast.success("Prodotto creato");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-2xl text-brand">
          {initial?.id ? `Modifica: ${initial.name}` : "Nuovo prodotto"}
        </h3>
        <button type="button" onClick={onClose} className="p-1.5 hover:bg-cream-warm">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nome"><Input required value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Slug (auto)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="lascia vuoto per auto-generare" /></Field>
        <Field label="Linea">
          <select className="w-full h-9 border border-soft bg-white px-3 text-sm" value={form.line} onChange={(e) => set("line", e.target.value)}>
            <option value="pet">Pet</option>
            <option value="human">Human</option>
          </select>
        </Field>
        <Field label="Status">
          <select className="w-full h-9 border border-soft bg-white px-3 text-sm" value={form.status} onChange={(e) => set("status", e.target.value)}>
            <option value="draft">Bozza</option>
            <option value="published">Pubblicato</option>
            <option value="coming_soon">Prossimamente</option>
          </select>
        </Field>
        <Field label="Categoria"><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
        <Field label="Quantità (es. 120 compresse)"><Input value={form.quantity_label} onChange={(e) => set("quantity_label", e.target.value)} /></Field>
        <Field label="URL Amazon" className="md:col-span-2"><Input value={form.amazon_url} onChange={(e) => set("amazon_url", e.target.value)} /></Field>
        <Field label="URL immagine principale" className="md:col-span-2"><Input value={form.image} onChange={(e) => set("image", e.target.value)} /></Field>
        <Field label="Descrizione breve" className="md:col-span-2">
          <textarea rows={2} className="w-full border border-soft bg-white p-3 text-sm rounded-none" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} />
        </Field>
        <Field label="Descrizione lunga" className="md:col-span-2">
          <textarea rows={4} className="w-full border border-soft bg-white p-3 text-sm rounded-none" value={form.long_description} onChange={(e) => set("long_description", e.target.value)} />
        </Field>
        <Field label="Badges (uno per riga)"><textarea rows={4} className="w-full border border-soft bg-white p-2 text-sm" value={(form.badges || []).join("\n")} onChange={(e) => setArr("badges", e.target.value)} /></Field>
        <Field label="Benefici (uno per riga)"><textarea rows={4} className="w-full border border-soft bg-white p-2 text-sm" value={(form.benefits || []).join("\n")} onChange={(e) => setArr("benefits", e.target.value)} /></Field>
        <Field label="Specie (uno per riga: cane, gatto…)"><textarea rows={3} className="w-full border border-soft bg-white p-2 text-sm" value={(form.species || []).join("\n")} onChange={(e) => setArr("species", e.target.value)} /></Field>
        <Field label="Avvertenze (una per riga)"><textarea rows={3} className="w-full border border-soft bg-white p-2 text-sm" value={(form.warnings || []).join("\n")} onChange={(e) => setArr("warnings", e.target.value)} /></Field>
        <Field label="Composizione (testo libero)" className="md:col-span-2">
          <textarea rows={3} className="w-full border border-soft bg-white p-3 text-sm rounded-none" value={form.ingredients_composition} onChange={(e) => set("ingredients_composition", e.target.value)} />
        </Field>
        <Field label="Meta title"><Input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} /></Field>
        <Field label="Meta description"><Input value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">
          Annulla
        </button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial hover:bg-[#0f2020] disabled:opacity-50">
          {loading ? "…" : "Salva"}
        </button>
      </div>
    </form>
  );
}

/* ---------- Articles ---------- */
function ArticlesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/articles").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare l'articolo?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Articolo eliminato");
    load();
  };

  if (editing) return <ArticleForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-ink">{items.length} articoli totali</div>
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial">
          <Plus className="h-3.5 w-3.5" /> Nuovo articolo
        </button>
      </div>
      <div className="bg-white border border-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream-warm">
            <tr><Th>Titolo</Th><Th>Categoria</Th><Th>Status</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-soft">
                <Td className="max-w-md">{a.title}</Td>
                <Td className="text-xs">{a.category}</Td>
                <Td className="text-xs">{a.status}</Td>
                <Td className="text-right">
                  <button onClick={() => setEditing(a)} className="p-1.5 hover:bg-cream-warm"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(a.id)} className="p-1.5 hover:bg-cream-warm text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArticleForm({ initial, onClose, onSaved }) {
  const empty = { title: "", slug: "", category: "", excerpt: "", content: "", image: "", author: "Redazione BioNutrix", read_time: 4, status: "draft", meta_title: "", meta_description: "", translations: {} };
  const [form, setForm] = useState(initial || empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/articles/${initial.id}`, form);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/admin/articles", form);
        toast.success("Articolo creato");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-2xl text-brand">{initial?.id ? "Modifica articolo" : "Nuovo articolo"}</h3>
        <button type="button" onClick={onClose} className="p-1.5 hover:bg-cream-warm"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Titolo" className="md:col-span-2"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Slug (auto)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
        <Field label="Categoria"><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
        <Field label="Autore"><Input value={form.author} onChange={(e) => set("author", e.target.value)} /></Field>
        <Field label="Tempo di lettura (min)"><Input type="number" value={form.read_time} onChange={(e) => set("read_time", parseInt(e.target.value || "0"))} /></Field>
        <Field label="URL immagine" className="md:col-span-2"><Input value={form.image} onChange={(e) => set("image", e.target.value)} /></Field>
        <Field label="Estratto" className="md:col-span-2">
          <textarea rows={2} className="w-full border border-soft bg-white p-3 text-sm" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
        </Field>
        <Field label="Contenuto (HTML)" className="md:col-span-2">
          <textarea rows={10} className="w-full border border-soft bg-white p-3 text-sm font-mono" value={form.content} onChange={(e) => set("content", e.target.value)} />
        </Field>
        <Field label="Status">
          <select className="w-full h-9 border border-soft bg-white px-3 text-sm" value={form.status} onChange={(e) => set("status", e.target.value)}>
            <option value="draft">Bozza</option>
            <option value="published">Pubblicato</option>
          </select>
        </Field>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">Annulla</button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{loading ? "…" : "Salva"}</button>
      </div>
    </form>
  );
}

/* ---------- FAQ ---------- */
function FaqsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare la FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    toast.success("FAQ eliminata");
    load();
  };

  if (editing) {
    return <FaqForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-ink">{items.length} FAQ totali</div>
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial">
          <Plus className="h-3.5 w-3.5" /> Nuova FAQ
        </button>
      </div>
      <div className="bg-white border border-soft">
        {items.map((f) => (
          <div key={f.id} className="p-4 border-b border-soft last:border-b-0 flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-editorial text-muted-ink mb-1">
                {f.category} · order {f.order} · {f.published ? "pub" : "hidden"}
              </div>
              <div className="text-brand font-medium">{f.question}</div>
              <div className="text-muted-ink text-sm">{f.answer}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setEditing(f)} className="p-1.5 hover:bg-cream-warm"><Edit2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(f.id)} className="p-1.5 hover:bg-cream-warm text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqForm({ initial, onClose, onSaved }) {
  const empty = { category: "general", question: "", answer: "", order: 0, published: true, translations: {} };
  const [form, setForm] = useState(initial || empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/faqs/${initial.id}`, form);
      } else {
        await api.post("/admin/faqs", form);
      }
      toast.success("FAQ salvata");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-2xl text-brand">{initial?.id ? "Modifica FAQ" : "Nuova FAQ"}</h3>
        <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Categoria">
          <select className="w-full h-9 border border-soft bg-white px-3 text-sm" value={form.category} onChange={(e) => set("category", e.target.value)}>
            <option value="general">Generali</option>
            <option value="product">Prodotto</option>
            <option value="quality">Qualità</option>
            <option value="human">Linea Human</option>
          </select>
        </Field>
        <Field label="Ordine"><Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0"))} /></Field>
        <Field label="Domanda" className="md:col-span-2"><Input required value={form.question} onChange={(e) => set("question", e.target.value)} /></Field>
        <Field label="Risposta" className="md:col-span-2">
          <textarea rows={4} className="w-full border border-soft bg-white p-3 text-sm" value={form.answer} onChange={(e) => set("answer", e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} />
          Pubblicata
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">Annulla</button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{loading ? "…" : "Salva"}</button>
      </div>
    </form>
  );
}

/* ---------- Reviews ---------- */
function ReviewsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({ name: "", rating: 5, text: "", source: "Cliente verificato", published: false, product_slug: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", form);
      toast.success("Recensione creata");
      setForm({ name: "", rating: 5, text: "", source: "Cliente verificato", published: false, product_slug: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/admin/reviews/${id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="font-display text-xl text-brand mb-3">Recensioni caricate</h3>
        <div className="bg-white border border-soft">
          {items.length === 0 && <div className="p-6 text-muted-ink text-sm">Nessuna recensione inserita.</div>}
          {items.map((r) => (
            <div key={r.id} className="p-4 border-b border-soft last:border-b-0">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-brand font-medium text-sm">{r.name} · {"★".repeat(r.rating)}</div>
                  <div className="text-[10px] uppercase tracking-editorial text-muted-ink">{r.source} · {r.published ? "pub" : "bozza"}</div>
                  <p className="text-sm text-muted-ink mt-1">{r.text}</p>
                </div>
                <button onClick={() => del(r.id)} className="p-1.5 hover:bg-cream-warm text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={save} className="bg-white border border-soft p-6 space-y-3">
        <h3 className="font-display text-xl text-brand">Aggiungi recensione autorizzata</h3>
        <Field label="Nome"><Input required value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Rating (1-5)"><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => set("rating", parseInt(e.target.value))} /></Field>
        <Field label="Testo"><textarea rows={3} className="w-full border border-soft bg-white p-2 text-sm" value={form.text} onChange={(e) => set("text", e.target.value)} /></Field>
        <Field label="Fonte / Provenienza"><Input value={form.source} onChange={(e) => set("source", e.target.value)} /></Field>
        <Field label="Prodotto (slug, opzionale)"><Input value={form.product_slug} onChange={(e) => set("product_slug", e.target.value)} placeholder="articolax" /></Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} />
          Pubblica subito
        </label>
        <button type="submit" className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial">Salva</button>
      </form>
    </div>
  );
}

/* ---------- Leads ---------- */
function LeadsPanel() {
  const [kind, setKind] = useState("contact");
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/admin/leads", { params: { kind } }).then((r) => setItems(r.data || []));
  }, [kind]);

  const download = () => {
    const token = localStorage.getItem("bnx_token");
    const url = `${API}/admin/leads/export?kind=${kind}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((csv) => {
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `bionutrix-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {[
          { id: "contact", label: "Contatti" },
          { id: "b2b", label: "B2B" },
          { id: "newsletter", label: "Newsletter" },
          { id: "human_notify", label: "Notify Human" },
        ].map((k) => (
          <button
            key={k.id}
            onClick={() => setKind(k.id)}
            className={`px-4 py-2 text-xs uppercase tracking-editorial border ${
              kind === k.id ? "bg-brand text-white border-brand" : "border-soft text-brand hover:bg-cream-warm"
            }`}
          >
            {k.label}
          </button>
        ))}
        <button
          onClick={download}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-soft px-4 py-2 text-xs uppercase tracking-editorial hover:bg-cream-warm"
          data-testid="admin-leads-export"
        >
          <Download className="h-3.5 w-3.5" />
          Esporta CSV
        </button>
      </div>
      <div className="bg-white border border-soft overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-6 text-muted-ink text-sm">Nessun lead in questa categoria.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-cream-warm">
              <tr>
                {Object.keys(items[0]).filter((k) => k !== "id").map((k) => <Th key={k}>{k}</Th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-soft">
                  {Object.entries(l).filter(([k]) => k !== "id").map(([k, v]) => (
                    <Td key={k} className="max-w-xs truncate text-xs">
                      {typeof v === "boolean" ? (v ? "sì" : "no") : Array.isArray(v) ? v.join(", ") : String(v ?? "")}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsPanel() {
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { api.get("/site-settings").then((r) => setForm(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/site-settings", form);
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!form) return <div className="text-muted-ink">Caricamento…</div>;

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        ["company_legal_name", "Ragione sociale"],
        ["vat_number", "Partita IVA"],
        ["fiscal_code", "Codice fiscale"],
        ["rea_number", "Numero REA"],
        ["legal_address", "Sede legale"],
        ["operational_address", "Sede operativa"],
        ["email", "Email"],
        ["pec", "PEC"],
        ["phone", "Telefono"],
        ["business_hours", "Orari"],
        ["instagram", "Instagram URL"],
        ["facebook", "Facebook URL"],
        ["linkedin", "LinkedIn URL"],
      ].map(([k, l]) => (
        <Field key={k} label={l}><Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>
      ))}
      <div className="md:col-span-2">
        <button type="submit" className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial">Salva</button>
      </div>
    </form>
  );
}

/* ---------- Utils ---------- */
function Th({ children }) {
  return <th className="text-left px-4 py-2 text-[10px] uppercase tracking-editorial text-muted-ink font-normal">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-brand ${className}`}>{children}</td>;
}
function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-editorial text-muted-ink mb-1.5">{label}</span>
      {children}
    </label>
  );
}
